<template>
    <form @submit.prevent="submit(form)">
        <div class="flex flex-wrap p-8 mb-8 -mr-6">
            <TextInput
                v-model="form.name"
                :error="form.errors.name"
                class="w-full pb-8 pr-6 lg:w-1/2"
                :label="__('Name')"
            />

            <div class="w-full pb-8 pr-6 lg:w-1/2">
                <label class="form-label">Huvudkategori</label>

                <VueMultiselect
                    v-model="selectedParent"
                    label="name"
                    track-by="id"
                    :options="categories"
                    :close-on-select="true"
                    :clear-on-select="false"
                    :placeholder="__('Select one')"
                    @select="onParentChange"
                    @remove="onParentRemove"
                />

                <div v-if="form.errors.parent_id" class="form-error">{{ form.errors.parent_id }}</div>
                <template v-if="!form.parent_id">
                </template>
            </div>

            <template v-if="!form.parent_id">
                <SelectInput
                    v-model="form.customer_target"
                    class="w-full pb-8 pr-6 lg:w-1/2"
                    label="Målgrupp"
                    :error="form.errors.customer_target"
                >
                    <option value="0">Endast företag</option>
                    <option value="1">Företag och privatpersoner</option>
                </SelectInput>

                <SelectInput
                    v-model="form.color_scheme"
                    class="w-full pb-8 pr-6 lg:w-1/2"
                    :error="form.errors.color_scheme"
                    :label="__('Color Scheme')"
                >
                    <option value="default">Standard</option>    
                    <option value="blue">Pfizer Pro</option>
                    <option value="purple">Eliquis</option>
                </SelectInput>

                <TextareaInput
                    v-model="form.delivery_description"
                    class="w-full pb-8 pr-6 lg:w-1/1"
                    :error="form.errors.delivery_description"
                    :label="__('Delivery Description')"
                />

                <div class="w-full pb-8 pr-6 lg:w-1/1">
                    <TextInput
                        v-model="form.gcma"
                        :error="form.errors.gcma"
                        class="w-full pb-8 pr-6 lg:w-1/1"
                        :label="__('GCMA')"
                    />
                </div>

                <div class="w-full pb-8 pr-6 lg:w-1/1">
                    <label class="form-label">{{ __('Receivers') }}:</label>

                    <VueMultiselect
                        v-model="form.receivers"
                        :class="{ error: form.errors.receivers }"
                        :options="receiver_list ?? []"
                        :searchable="false"
                        :multiple="true"
                        :taggable="true"
                        :close-on-select="false"
                        tag-placeholder="Lägg till person"
                        placeholder="Skriv för att söka och lägga till person"
                        @tag="addReceiver"
                    />

                    <div v-if="form.errors.receivers" class="form-error">
                        {{ form.errors.receivers }}
                    </div>
                </div>

                <div class="w-full pb-8 pr-6 lg:w-1/1" v-if="products?.length > 0">
                    <label class="form-label">{{ __('Featured products (Max 10)') }}:</label>

                    <VueMultiselect
                        v-model="form.featured"
                        label="name"
                        track-by="id"
                        :class="{ error: form.errors.featured }"
                        :options="products ?? []"
                        :multiple="true"
                        :taggable="true"
                        :close-on-select="false"
                        :tag-placeholder="__('Add this featured product')"
                        :placeholder="__('Type to search or add featured products')"
                    />

                    <div v-if="form.errors.featured" class="form-error">
                        {{ form.errors.featured }}
                    </div>
                </div>


                <div class="w-full pb-8 pr-6 lg:w-1/1">
                    <h2 class="text-[24px] mb-[30px]">Inställningar för orderbekräftelsesida</h2>

                    <div class="flex w-full justify-start items-center mb-[30px]">
                        <FileInput
                            v-if="!form.t_bg && !previewImage"
                            v-model="form.t_bg"
                            type="file"
                            class="w-full pb-8 pr-6 lg:w-1/2"
                            accept="image/*"
                            :error="null"
                            label="Ladda upp bakgrundsbild"
                        />

                        <img
                            v-if="previewImage"
                            :src="previewImage"
                            alt=""
                            class="max-w-[150px]"
                        />

                        <span
                            v-if="previewImage"
                            class="ml-4 cursor-pointer btn"
                            @click="resetImage"
                        >
                            Ta bort bild
                        </span>
                    </div>

                    <div class="w-full pb-8">
                        <QuillEditor
                            v-model:content="form.t_text"
                            ref="description"
                            label="Content"
                            content-type="html"
                            :error="null"
                            :options="editorOption"
                        />
                    </div>

                    <div class="w-full">
                        <TextInput
                            v-model="form.t_cta_txt"
                            class="w-full pb-8 pr-6 lg:w-1/2"
                            :error="form.errors.t_cta_txt"
                            label="Knapptext"
                        />
                    </div>

                    <div class="w-full">
                        <TextInput
                            v-model="form.t_cta_link"
                            class="w-full pb-8 pr-6 lg:w-1/2"
                            :error="form.errors.t_cta_link"
                            label="Länka knappen till"
                        />
                    </div>

                    <div class="w-full">
                        <TextInput
                            v-model="form.t_iframe"
                            class="w-full pb-8 pr-6 lg:w-1/2"
                            :error="form.errors.t_iframe"
                            label="HTML-kod för iframe"
                            placeholder="<iframe src='...'></iframe>"
                        />
                    </div>
                </div>
            </template>
        </div>

        <div class="flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-gray-50">
            <button v-if="form.id" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">
                {{ __('Delete') }}
            </button>

            <LoadingButton :loading="form.processing" class="btn-save" type="submit">
                {{ label }}
            </LoadingButton>
        </div>
    </form>
</template>

<script>
    import { QuillEditor } from '@vueup/vue-quill'
    import VueMultiselect  from 'vue-multiselect'
    import Layout 		   from '@/Shared/Layout'
    import BaseSelect      from '@/Shared/BaseSelect'
    import TextInput 	   from '@/Shared/TextInput'
    import TextareaInput   from '@/Shared/TextareaInput'
    import SelectInput 	   from '@/Shared/SelectInput'
    import FileInput 	   from '@/Shared/FileInput'
    import LoadingButton   from '@/Shared/LoadingButton'
    import '@vueup/vue-quill/dist/vue-quill.snow.css'

    export default {
        components: {
            QuillEditor,
            FileInput,
            VueMultiselect,
            LoadingButton,
            SelectInput,
            TextInput,
            TextareaInput,
            BaseSelect,
        },
        layout: Layout,
        remember: 'form',
        props: {
            model: {
                category: Object,
                type: Object,
                default: () => ({
                    name: null,
                    parent_id: null,
                    receivers: [],
                    featured: [],
                    delivery_description: null,
                    customer_target: 0,
                    color_scheme: 'default',
                    t_bg_file: null,
                    t_bg: null,
                    t_text: null,
                    t_cta_txt: null,
                    t_cta_link: null,
                    t_iframe: null
                })
            },
            submit: {
                type: Function,
                default: () => {},
            },
            label: {
                type: String,
                default: () => { return 'Skapa kategori' },
            },
            receiver_list: Array,
            all_users: Array,
        },
        data() {
            return {
                receivers: [],
                categories: [],
                previewImage: null,
                selectedParent: null,
                featured: this.model.featured,
                products: this.model.products,
                form: this.$inertia.form(this.model),
                editorOption: {
                placeholder: 'Skriv text här...',
                readOnly: false,
                theme: 'snow',
                modules: {
                        toolbar: {
                            container: [
                                ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
                                ['blockquote', 'code-block', 'link'],
                                [{ 'header': 1 }, { 'header': 2 }],               // custom button values
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
                                [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
                                [{ 'direction': 'rtl' }],                         // text direction
                                [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
                                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
                                [{ 'font': [] }],
                                [{ 'align': [] }]
                            ]
                        }
                    }
                }
            }
        },
        watch: {
            form: {
                deep: true,
                immediate: true,
                handler() {
                    if (this.form.t_bg && typeof this.form.t_bg === 'object') {
                        this.previewImage = URL.createObjectURL(this.form.t_bg)
                    } else {
                        this.previewImage = this.form.t_bg
                    }
                }
            }
        },
        methods: {
            validEmail(email) {
                const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                return re.test(String(email).toLowerCase())
            },
            onParentChange(selectedParent) {
                this.form.parent_id = selectedParent.id
            },
            onParentRemove() {
                this.form.parent_id = null
            },
            addReceiver(newReceiver) {
                if (this.receivers.find((option) => option === newReceiver)) {
                    return
                }

                if (!this.validEmail(newReceiver)) {
                    alert('Felaktig e-postadress')
                    return
                }

                this.receivers.push(newReceiver)
                this.form.receivers.push(newReceiver)
            },
            fetchCategories() {
                fetch('/api/categories?parent=true&per_page=all').then((response) => response.json()).then(data => {
                    this.categories = data.categories

                    if (this.form.parent_id) {
                        this.selectedParent = this.categories.find((category) => category.id === this.form.parent_id)
                    }
                })
            },
            destroy() {
                if (confirm('Är du säker på att du vill ta bort denna kategori?')) {
                    this.$inertia.delete(`/categories/${ this.form.id }`)
                }
            },
            resetImage() {
                this.form.t_bg = null
                this.previewImage = null
            }
        },
        mounted() {
            this.fetchCategories()
        }
    }
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
